import { useState, type FormEvent } from 'react';

export default function SupportLeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    website: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const response = await fetch('https://readdy.ai/api/form/d6te4mgq778icnc8unj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          website: formData.website,
        }).toString(),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', website: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="support-form" className="py-14 sm:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-['Playfair_Display'] font-bold text-[#1A2B4C] mb-4 sm:mb-6 leading-tight">
            Стоимость и условия
          </h2>
          <div className="w-16 h-1 bg-[#C5A880] mx-auto mb-4 sm:mb-6 rounded-full"></div>
          <div className="inline-block bg-[#1A2B4C] rounded-xl sm:rounded-2xl px-7 sm:px-10 py-3 sm:py-4 mb-3 sm:mb-4">
            <span className="text-3xl sm:text-4xl font-bold text-[#C5A880] font-['Playfair_Display']">3 000 ₽</span>
            <span className="text-white/70 text-sm ml-2 font-['Montserrat']">/ месяц</span>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm font-['Montserrat'] max-w-xl mx-auto text-justify">
            Это дешевле, чем один день рекламного бюджета, но эффект от этой работы остаётся с вами навсегда.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* LEFT: Photo + bio */}
          <div className="flex flex-col items-center lg:items-start">
            <div className="relative w-full max-w-xs sm:max-w-sm mx-auto lg:mx-0">
              <div className="rounded-2xl sm:rounded-3xl overflow-hidden w-full">
                <img
                  src="https://static.readdy.ai/image/621f76ab0c5d0ae7827482fe9856efef/353a1b52fb77cf7e2c624e877b9048b3.png"
                  alt="Ирина Шарова-Ялынпала — Маркетолог-стратег"
                  className="w-full h-auto object-contain"
                />
              </div>
              {/* Name badge */}
              <div className="mt-4 sm:mt-6 bg-white border border-[#C5A880]/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <p className="text-[#1A2B4C] font-bold text-base sm:text-lg font-['Playfair_Display'] mb-1">
                  Ирина Шарова-Ялынпала
                </p>
                <p className="text-[#C5A880] text-xs sm:text-sm font-semibold font-['Montserrat'] mb-2">
                  Маркетолог-стратег
                </p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed font-['Montserrat'] text-justify">
                  Я не просто слежу за кодом, я слежу за тем, чтобы ваш бизнес оставался лидером в своей нише.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div>
            <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-5 sm:p-10">
              <h3 className="text-lg sm:text-2xl font-bold text-[#1A2B4C] mb-2 font-['Playfair_Display'] leading-snug">
                Забронировать место на сопровождение
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm font-['Montserrat'] mb-5 sm:mb-8 leading-relaxed text-justify">
                Оставьте заявку — разберём вашу ситуацию и покажем, как именно ваш бизнес будет выглядеть с системой ежемесячного SEO-сопровождения.
              </p>

              <form
                onSubmit={handleSubmit}
                className="space-y-4 sm:space-y-5"
                data-readdy-form
                id="support-page-form"
              >
                {/* Name */}
                <div>
                  <label htmlFor="support-name" className="block text-xs font-semibold text-gray-700 mb-2 font-['Montserrat']">
                    Ваше имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="support-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#C5A880]/40 focus:border-[#C5A880] transition-all text-sm font-['Montserrat'] outline-none"
                    placeholder="Иван Иванов"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="support-phone" className="block text-xs font-semibold text-gray-700 mb-2 font-['Montserrat']">
                    Телефон <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="support-phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#C5A880]/40 focus:border-[#C5A880] transition-all text-sm font-['Montserrat'] outline-none"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>

                {/* Website */}
                <div>
                  <label htmlFor="support-website" className="block text-xs font-semibold text-gray-700 mb-2 font-['Montserrat']">
                    Ссылка на ваш сайт{' '}
                    <span className="text-gray-400 text-xs font-normal">(если есть)</span>
                  </label>
                  <input
                    type="text"
                    id="support-website"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#C5A880]/40 focus:border-[#C5A880] transition-all text-sm font-['Montserrat'] outline-none"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-[#1A2B4C] hover:bg-[#253D6B] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-['Montserrat'] cursor-pointer mt-2 leading-tight"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Отправка...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 flex-wrap">
                      Забронировать место на сопровождение
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                </button>

                {/* Status messages */}
                {status === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm font-['Montserrat']">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Заявка отправлена! Ирина свяжется с вами в ближайшее время.
                  </div>
                )}
                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm font-['Montserrat']">
                    <i className="ri-error-warning-line mr-2"></i>
                    Произошла ошибка. Попробуйте ещё раз.
                  </div>
                )}

                <p className="text-xs text-gray-400 text-center font-['Montserrat']">
                  Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
